import { useCallback, useMemo } from "react";

import { useTranslate } from "@similarweb/i18n-provider";
import { NoBorderButtonWrap, SimpleChipItemWrap } from "@similarweb/ui-components";

import { TreeSelectCombobox } from "app/pages/sales-intelligence/common-components/dropdown/TreeSelectCombobox/TreeSelectCombobox";
import type { IndustryNodeDto, IndustryNodeId } from "app/pages/sales-intelligence/domain/industry/types/taxonomy";
import { createIndustrySearchIndex } from "app/pages/sales-intelligence/modules/industry/services/search/createIndustrySearchIndex";
import { createSingleSelectIndustryTreeViewModel } from "app/pages/sales-intelligence/modules/industry/view/industry-tree/factory";
import leadsSearchFiltersI18nKeys from "app/pages/sales-intelligence/modules/leads-search-filters/utils/i18n";
import { allTrackers } from "app/services/track/track";

import { toCompetitorIndustryIndex } from "../../helpers/toCompetitorIndustryIndex";
import useCompetitiveFilter from "../../hooks/useCompetitiveFilter";
import { StyledChipWrap } from "../CompetitiveFilter/styles";

const TRACKING_CLEARED = "Clear Filter";

// This tree holds only the industries the competitors on screen fall into, so it is routinely a few
// rows — the panel's reserved height would render most of the popover as empty space.
const FIT_CONTENT_HEIGHT = 0;

type Props = {
    nodes: readonly IndustryNodeDto[];
};

/**
 * The taxonomy counterpart of `CompetitiveCategoryFilter`: the same tree picker Lead Generation uses,
 * holding one node instead of many.
 *
 * The options come from the served filter tree, not the taxonomy endpoint, so everything the legacy
 * control did keeps working untouched — only industries present in the results are offered, and picking
 * one narrows the list to that branch on the refetch.
 */
export const CompetitiveIndustryTaxonomyFilter = ({ nodes }: Props) => {
    const { translate, getLocale } = useTranslate();
    const locale = getLocale();
    const { onSelectIndustryTaxonomy, selectedIndustryTaxonomyId } = useCompetitiveFilter();

    // The URL is the other boundary where an opaque id enters typed; the tree is what gives it meaning.
    const selectedId = selectedIndustryTaxonomyId as IndustryNodeId | undefined;

    const taxonomy = useMemo(() => toCompetitorIndustryIndex(nodes), [nodes]);
    // Created once: the engine caches its normalised documents on the taxonomy reference, and a fresh
    // instance per render would throw that cache away on every keystroke.
    const searchIndex = useMemo(() => createIndustrySearchIndex(), []);

    const searchHits = useCallback(
        (query: string) => searchIndex.searchHits({ taxonomy, query, locale }),
        [searchIndex, taxonomy, locale],
    );

    const viewModel = useMemo(
        () => createSingleSelectIndustryTreeViewModel({ taxonomy, value: selectedId, searchHits }),
        [taxonomy, selectedId, searchHits],
    );

    // Selecting refetches through a full route reload, so the tree remounts collapsed. Opening the
    // picked node again is what makes the reopened dropdown continue the user's train of thought.
    const initialExpanded = useMemo(() => (selectedId ? new Set([selectedId]) : undefined), [selectedId]);

    const selectedName = selectedId ? taxonomy.byId.get(selectedId)?.name : undefined;

    const handleChange = (next: readonly IndustryNodeId[]) => {
        // The single-select view model yields either nothing or one id; the array type cannot say so.
        const [nextId] = next;

        allTrackers.dispatchTrackEvent(
            "Drop Down",
            "click",
            `Table/Category/${nextId ? (taxonomy.byId.get(nextId)?.name ?? nextId) : TRACKING_CLEARED}`,
        );
        onSelectIndustryTaxonomy(nextId ?? null);
    };

    // The chip's own close handler stops the click before it reaches the combobox trigger, so clearing
    // does not also reopen the popover. Its fallback label covers a filter the tree cannot name — a stale
    // bookmark, or an id from the other classification; naming the filter beats claiming "all" while
    // results are filtered.
    const trigger = selectedId ? (
        <SimpleChipItemWrap
            text={selectedName ?? translate(leadsSearchFiltersI18nKeys.industryTaxonomyName())}
            onCloseItem={() => handleChange([])}
        />
    ) : (
        <NoBorderButtonWrap>{translate("common.category.all")}</NoBorderButtonWrap>
    );

    return (
        <StyledChipWrap>
            <TreeSelectCombobox
                selection="single"
                dataAutomation="competitive-landscape-industry-taxonomy-filter"
                viewModel={viewModel}
                trigger={trigger}
                contentMinHeight={FIT_CONTENT_HEIGHT}
                initialExpanded={initialExpanded}
                searchPlaceholder={translate(leadsSearchFiltersI18nKeys.industryTaxonomyDdPlaceholder())}
                expandLabel={translate(leadsSearchFiltersI18nKeys.industryTaxonomyExpandAria())}
                collapseLabel={translate(leadsSearchFiltersI18nKeys.industryTaxonomyCollapseAria())}
                onChange={handleChange}
            />
        </StyledChipWrap>
    );
};
